import { useId, useMemo } from 'react';
import { seeded } from '../../lib/living';

/* Procedural, species-true illustrations (100×100 SVG).
 * A "look" describes what the organism actually looks like — cap shape and
 * colour, gills vs. pores vs. spines, leaf shape, flowers, berries — and the
 * matching drawer renders it with gradient shading for a 3D feel.
 */

// ---------- colour helpers ----------
function hexToRgb(h) {
    const n = parseInt(h.replace('#', ''), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
export function shade(hex, amt) {
    const [r, g, b] = hexToRgb(hex);
    const f = (c) => Math.round(amt >= 0 ? c + (255 - c) * amt : c * (1 + amt));
    return `#${[f(r), f(g), f(b)].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

function Grad({ id, color, cx = '38%', cy = '30%' }) {
    return (
        <radialGradient id={id} cx={cx} cy={cy} r="75%">
            <stop offset="0%" stopColor={shade(color, 0.45)} />
            <stop offset="55%" stopColor={color} />
            <stop offset="100%" stopColor={shade(color, -0.35)} />
        </radialGradient>
    );
}
function LinGrad({ id, color, dir = 'h' }) {
    return (
        <linearGradient id={id} x1="0" y1="0" x2={dir === 'h' ? '1' : '0'} y2={dir === 'h' ? '0' : '1'}>
            <stop offset="0%" stopColor={shade(color, -0.25)} />
            <stop offset="40%" stopColor={shade(color, 0.25)} />
            <stop offset="100%" stopColor={shade(color, -0.3)} />
        </linearGradient>
    );
}
const Shadow = ({ w = 30, y = 90 }) => <ellipse cx="50" cy={y} rx={w} ry={w * 0.16} fill="#000" opacity="0.18" />;

// ================= MUSHROOMS =================

function capPath(shape, cx, top, w, h) {
    const l = cx - w / 2;
    const r = cx + w / 2;
    const base = top + h;
    switch (shape) {
        case 'flat': return `M${l} ${base} Q${l} ${top + h * 0.35} ${cx} ${top + h * 0.3} Q${r} ${top + h * 0.35} ${r} ${base} Q${cx} ${base + h * 0.18} ${l} ${base}Z`;
        case 'bell': return `M${l + w * 0.08} ${base} C${l} ${top + h * 0.2} ${cx - w * 0.2} ${top} ${cx} ${top} C${cx + w * 0.2} ${top} ${r} ${top + h * 0.2} ${r - w * 0.08} ${base} Q${cx} ${base + h * 0.12} ${l + w * 0.08} ${base}Z`;
        case 'conical': return `M${l + w * 0.1} ${base} L${cx} ${top} L${r - w * 0.1} ${base} Q${cx} ${base + h * 0.12} ${l + w * 0.1} ${base}Z`;
        case 'depressed': return `M${l} ${base - h * 0.1} Q${l + w * 0.05} ${top} ${cx - w * 0.25} ${top + h * 0.05} Q${cx} ${top + h * 0.35} ${cx + w * 0.25} ${top + h * 0.05} Q${r - w * 0.05} ${top} ${r} ${base - h * 0.1} Q${cx} ${base + h * 0.15} ${l} ${base - h * 0.1}Z`;
        case 'umbo': return `M${l} ${base} Q${l + w * 0.08} ${top + h * 0.3} ${cx - w * 0.12} ${top + h * 0.25} Q${cx} ${top - h * 0.1} ${cx + w * 0.12} ${top + h * 0.25} Q${r - w * 0.08} ${top + h * 0.3} ${r} ${base} Q${cx} ${base + h * 0.12} ${l} ${base}Z`;
        default: return `M${l} ${base} C${l} ${top - h * 0.05} ${r} ${top - h * 0.05} ${r} ${base} Q${cx} ${base + h * 0.15} ${l} ${base}Z`;
    }
}

function Mushroom({ uid, a, cx = 50, scale = 1, rnd }) {
    const w = (a.capW || 60) * scale;
    const h = (a.capH || 26) * scale;
    const stemW = (a.stemW || 13) * scale;
    const top = a.capTop ?? 22 + (1 - scale) * 40;
    const base = top + h;
    const ground = 88;
    const bulb = a.bulb ? stemW * 0.45 : 0;
    const stemPath = `M${cx - stemW / 2} ${base - 2} C${cx - stemW / 2 - 1} ${ground - 14} ${cx - stemW / 2 - bulb} ${ground - 4} ${cx - stemW / 2 - bulb * 0.4} ${ground}
        L${cx + stemW / 2 + bulb * 0.4} ${ground} C${cx + stemW / 2 + bulb} ${ground - 4} ${cx + stemW / 2 + 1} ${ground - 14} ${cx + stemW / 2} ${base - 2}Z`;
    const underY = base - 1;
    return (
        <g>
            <path d={stemPath} fill={`url(#${uid}-stem)`} />
            {a.net && Array.from({ length: 6 }, (_, i) => (
                <path key={i} d={`M${cx - stemW / 2 + 2} ${base + 6 + i * 5} q${stemW / 2 - 2} 3 ${stemW - 4} 0`} stroke={shade(a.stem, -0.25)} strokeWidth="0.6" fill="none" opacity="0.6" />
            ))}
            {a.scalyStem && Array.from({ length: 7 }, (_, i) => (
                <path key={i} d={`M${cx - stemW / 2 + 1} ${base + 4 + i * 5} l${stemW - 2} 2`} stroke={shade(a.stem, -0.4)} strokeWidth="1" opacity="0.5" />
            ))}
            {a.ring && <ellipse cx={cx} cy={base + (ground - base) * 0.25} rx={stemW * 0.85} ry={2.2 * scale} fill={shade(a.stem, 0.2)} stroke={shade(a.stem, -0.2)} strokeWidth="0.5" />}
            {a.volva && <path d={`M${cx - stemW * 0.9} ${ground} Q${cx - stemW} ${ground - 9} ${cx - stemW * 0.3} ${ground - 7} L${cx + stemW * 0.3} ${ground - 7} Q${cx + stemW} ${ground - 9} ${cx + stemW * 0.9} ${ground}Z`} fill="#f1ece2" stroke="#d7cfbf" strokeWidth="0.6" />}
            {/* underside: gills / pores / teeth */}
            {a.under && (
                <g>
                    <ellipse cx={cx} cy={underY} rx={w * 0.47} ry={h * 0.2} fill={a.under} />
                    {a.pores && Array.from({ length: 18 }, (_, i) => (
                        <circle key={i} cx={cx - w * 0.4 + (i % 9) * w * 0.1} cy={underY - 1 + Math.floor(i / 9) * 2.2} r={0.6 * scale} fill={shade(a.under, -0.3)} opacity="0.6" />
                    ))}
                    {a.teeth && Array.from({ length: 14 }, (_, i) => (
                        <path key={i} d={`M${cx - w * 0.42 + i * w * 0.064} ${underY} l0.8 ${3 + rnd() * 2.5}`} stroke={shade(a.under, -0.15)} strokeWidth="1.4" strokeLinecap="round" />
                    ))}
                    {!a.pores && !a.teeth && Array.from({ length: 13 }, (_, i) => (
                        <path key={i} d={`M${cx} ${underY - h * 0.12} L${cx - w * 0.45 + i * w * 0.075} ${underY + h * 0.08}`} stroke={shade(a.under, -0.3)} strokeWidth="0.5" opacity="0.7" />
                    ))}
                </g>
            )}
            <path d={capPath(a.shape, cx, top, w, h)} fill={`url(#${uid}-cap)`} />
            {a.zones && [0.55, 0.75].map((k) => (
                <path key={k} d={capPath(a.shape, cx, top + h * (1 - k) * 0.5, w * k, h * k)} fill="none" stroke={shade(a.cap, -0.25)} strokeWidth="0.8" opacity="0.5" />
            ))}
            {a.spots && Array.from({ length: 9 }, (_, i) => {
                const t = rnd();
                return <ellipse key={i} cx={cx - w * 0.36 + rnd() * w * 0.72} cy={top + h * 0.2 + t * h * 0.6} rx={1.6 * scale + rnd()} ry={1.2 * scale + rnd() * 0.6} fill="#fbf7ee" opacity="0.95" />;
            })}
            {a.scales && Array.from({ length: 16 }, (_, i) => (
                <path key={i} d={`M${cx - w * 0.38 + rnd() * w * 0.76} ${top + h * 0.2 + rnd() * h * 0.6} l1.6 1.2 l-1.8 0.6z`} fill={a.scales} opacity="0.85" />
            ))}
            {a.cracks && Array.from({ length: 7 }, (_, i) => (
                <path key={i} d={`M${cx - w * 0.3 + rnd() * w * 0.6} ${top + h * 0.3 + rnd() * h * 0.4} l${2 + rnd() * 3} ${rnd() * 2 - 1} l${1 + rnd() * 2} 1.5`} stroke={a.cracks} strokeWidth="0.8" fill="none" />
            ))}
            {a.slime && <path d={capPath(a.shape, cx, top, w * 0.7, h * 0.5)} transform={`translate(${-w * 0.08} ${-1})`} fill="#fff" opacity="0.28" />}
            {/* specular highlight */}
            <ellipse cx={cx - w * 0.18} cy={top + h * 0.35} rx={w * 0.14} ry={h * 0.12} fill="#fff" opacity="0.25" />
        </g>
    );
}

function Funnel({ a, uid, rnd, cx = 50, scale = 1, trumpet = false }) {
    const w = (trumpet ? 40 : 58) * scale;
    const topY = trumpet ? 18 : 30;
    const ground = 88;
    const neck = trumpet ? 6 : 9;
    const rimPts = Array.from({ length: 9 }, (_, i) => {
        const x = cx - w / 2 + (i * w) / 8;
        return `${x} ${topY + (i % 2 ? 2.5 : -1.5) * scale + rnd()}`;
    });
    const body = `M${rimPts[0]} Q${cx - w * 0.3} ${topY + 30 * scale} ${cx - neck * scale} ${ground - 18 * scale} L${cx - neck * scale * 0.8} ${ground} L${cx + neck * scale * 0.8} ${ground} L${cx + neck * scale} ${ground - 18 * scale} Q${cx + w * 0.3} ${topY + 30 * scale} ${rimPts[8]} Z`;
    return (
        <g>
            <path d={body} fill={`url(#${uid}-stem)`} />
            {!trumpet && Array.from({ length: 9 }, (_, i) => (
                <path key={i} d={`M${cx - w * 0.42 + i * w * 0.105} ${topY + 3} Q${cx - w * 0.2 + i * w * 0.05} ${topY + 22 * scale} ${cx - neck * 0.6 + i * 1.2} ${ground - 16}`} stroke={shade(a.cap, -0.25)} strokeWidth="0.7" fill="none" opacity="0.7" />
            ))}
            <ellipse cx={cx} cy={topY} rx={w / 2} ry={(trumpet ? 7 : 8) * scale} fill={`url(#${uid}-cap)`} />
            <ellipse cx={cx} cy={topY + 1} rx={w * 0.36} ry={(trumpet ? 4.5 : 4.6) * scale} fill={shade(a.inside || a.cap, trumpet ? -0.55 : -0.12)} />
            <path d={`M${rimPts.join(' L')}`} stroke={shade(a.cap, -0.3)} strokeWidth="0.8" fill="none" opacity="0.5" />
        </g>
    );
}

function Morel({ a, uid, rnd }) {
    const pits = [];
    for (let r = 0; r < 6; r++) {
        for (let c = 0; c < 5 - Math.floor(r / 3); c++) {
            const y = 18 + r * 7.5;
            const span = 10 + r * 2.8;
            pits.push(<ellipse key={`${r}-${c}`} cx={50 - span + c * (span * 2) / (4 - Math.floor(r / 3)) + (r % 2) * 2} cy={y + rnd()} rx={2.8} ry={3.4} fill={shade(a.cap, -0.55)} opacity="0.85" />);
        }
    }
    const capBottom = a.halfFree ? 50 : 62;
    return (
        <g>
            <path d={`M43 ${capBottom - 4} L42 88 L58 88 L57 ${capBottom - 4}Z`} fill={`url(#${uid}-stem)`} />
            <path d={`M${a.halfFree ? 38 : 33} ${capBottom} C30 30 42 8 50 8 C58 8 70 30 ${a.halfFree ? 62 : 67} ${capBottom} Q50 ${capBottom + 5} ${a.halfFree ? 38 : 33} ${capBottom}Z`} fill={`url(#${uid}-cap)`} />
            <g clipPath={`url(#${uid}-clip)`}>{pits}</g>
            <clipPath id={`${uid}-clip`}><path d={`M${a.halfFree ? 38 : 33} ${capBottom} C30 30 42 8 50 8 C58 8 70 30 ${a.halfFree ? 62 : 67} ${capBottom}Z`} /></clipPath>
        </g>
    );
}

function Truffle({ a, uid, rnd }) {
    const warts = Array.from({ length: 40 }, (_, i) => {
        const ang = rnd() * Math.PI * 2;
        const rr = rnd() * 22;
        return <circle key={i} cx={46 + Math.cos(ang) * rr} cy={60 + Math.sin(ang) * rr * 0.85} r={1 + rnd() * 1.3} fill={shade(a.cap, a.smooth ? 0.15 : -0.35)} opacity={a.smooth ? 0.3 : 0.8} />;
    });
    return (
        <g>
            <path d="M22 62 C20 40 40 32 50 36 C66 30 76 46 72 62 C74 80 56 86 46 84 C30 86 22 76 22 62Z" fill={`url(#${uid}-cap)`} />
            {warts}
            {/* sliced half showing marbled flesh */}
            <ellipse cx="74" cy="78" rx="14" ry="8" fill={a.flesh || '#d9c5a3'} stroke={shade(a.cap, -0.2)} strokeWidth="2" />
            {Array.from({ length: 5 }, (_, i) => (
                <path key={i} d={`M${63 + i * 4} ${73 + (i % 2) * 3} q3 4 6 1`} stroke="#fff" strokeWidth="0.9" fill="none" opacity="0.8" />
            ))}
        </g>
    );
}

function Puffball({ a, uid }) {
    return (
        <g>
            <path d="M16 70 C14 40 34 24 52 24 C72 24 88 42 84 68 C82 84 64 88 50 88 C32 88 18 84 16 70Z" fill={`url(#${uid}-cap)`} />
            {Array.from({ length: 22 }, (_, i) => (
                <path key={i} d={`M${24 + (i % 8) * 7} ${40 + Math.floor(i / 8) * 13} l2 1`} stroke={shade(a.cap, -0.2)} strokeWidth="0.8" opacity="0.6" />
            ))}
        </g>
    );
}

function Oyster({ a, uid }) {
    const shelf = (y, s, k) => (
        <g key={k}>
            <path d={`M20 ${y + 8} C24 ${y - 18 * s} ${70 + 12 * s} ${y - 22 * s} ${86} ${y - 2} C${84} ${y + 10} ${60} ${y + 14} ${22} ${y + 12}Z`} fill={`url(#${uid}-cap)`} />
            <path d={`M22 ${y + 12} C${50} ${y + 16} ${76} ${y + 10} ${86} ${y - 2} C${70} ${y + 22 * s} ${40} ${y + 24 * s} ${22} ${y + 12}Z`} fill={a.under || '#efe8dc'} />
            {Array.from({ length: 10 }, (_, i) => (
                <path key={i} d={`M24 ${y + 13} L${36 + i * 5.2} ${y + 12 + (i < 5 ? 10 : 6) * s}`} stroke={shade(a.under || '#efe8dc', -0.2)} strokeWidth="0.5" />
            ))}
        </g>
    );
    return (
        <g>
            <rect x="12" y="20" width="10" height="70" rx="3" fill="#6d5237" />
            <rect x="12" y="20" width="4" height="70" fill="#5a4330" />
            {shelf(30, 0.9, 'a')}
            {shelf(52, 1, 'b')}
            {shelf(72, 0.8, 'c')}
        </g>
    );
}

function Maned({ a, uid }) {
    return (
        <g>
            <path d="M22 44 C22 22 78 22 78 44 C80 58 70 64 50 64 C30 64 20 58 22 44Z" fill={`url(#${uid}-cap)`} />
            {Array.from({ length: 30 }, (_, i) => {
                const x = 22 + (i % 15) * 4 + (i >= 15 ? 2 : 0);
                const len = 12 + ((i * 7) % 10);
                return <path key={i} d={`M${x} ${48 + (i >= 15 ? 6 : 0)} l0.6 ${len}`} stroke={shade(a.cap, i % 3 ? -0.08 : -0.18)} strokeWidth="2.2" strokeLinecap="round" />;
            })}
            <rect x="46" y="20" width="10" height="8" rx="2" fill="#6d5237" />
        </g>
    );
}

function Coral({ a, uid, rnd }) {
    const lobes = Array.from({ length: 16 }, (_, i) => {
        const x = 24 + rnd() * 52;
        const y = 30 + rnd() * 42;
        const r = 9 + rnd() * 6;
        return <path key={i} d={`M${x - r} ${y} q${r * 0.3} ${-r} ${r} ${-r * 0.4} q${r * 0.7} ${-r * 0.5} ${r} ${r * 0.4} q${-r * 0.8} ${r * 0.2} ${-r * 2} 0z`} fill={`url(#${uid}-cap)`} stroke={shade(a.cap, -0.3)} strokeWidth="0.6" />;
    });
    return <g><ellipse cx="50" cy="80" rx="12" ry="8" fill={shade(a.cap, -0.2)} />{lobes}</g>;
}

function Jelly({ a, uid }) {
    if (a.frilly) {
        return (
            <g>
                {Array.from({ length: 9 }, (_, i) => {
                    const ang = (i / 9) * Math.PI * 2;
                    return <path key={i} d={`M50 60 Q${50 + Math.cos(ang) * 40} ${60 + Math.sin(ang) * 24 - 10} ${50 + Math.cos(ang + 0.4) * 26} ${60 + Math.sin(ang + 0.4) * 18}Z`} fill={`url(#${uid}-cap)`} opacity="0.85" stroke="#fff" strokeWidth="0.6" />;
                })}
            </g>
        );
    }
    return (
        <g>
            <rect x="10" y="30" width="80" height="12" rx="5" fill="#6d5237" transform="rotate(-8 50 36)" />
            <path d="M30 44 C18 50 18 76 38 80 C56 84 70 70 66 56 C62 46 50 52 46 58 C44 50 40 42 30 44Z" fill={`url(#${uid}-cap)`} opacity="0.92" />
            <path d="M34 54 C30 62 36 72 44 72" stroke={shade(a.cap, -0.4)} strokeWidth="1.2" fill="none" opacity="0.6" />
            <path d="M58 44 C70 40 80 52 72 60 C66 64 60 56 58 44Z" fill={`url(#${uid}-cap)`} opacity="0.85" />
        </g>
    );
}

function Inkcap({ a, uid }) {
    return (
        <g>
            <path d="M46 60 L45 88 L55 88 L54 60Z" fill="#f4f0e8" />
            <path d="M36 66 C33 40 38 10 50 10 C62 10 67 40 64 66 Z" fill={`url(#${uid}-cap)`} />
            {Array.from({ length: 18 }, (_, i) => (
                <path key={i} d={`M${39 + (i % 6) * 4.4} ${18 + Math.floor(i / 6) * 13} l2 3 l-3.5 0.5z`} fill="#c9b9a0" opacity="0.8" />
            ))}
            <path d="M36 66 Q40 72 43 66 Q46 74 50 66 Q54 73 57 66 Q60 71 64 66 L64 62 L36 62Z" fill="#2b2622" />
        </g>
    );
}

function Enoki({ a }) {
    return (
        <g>
            {Array.from({ length: 13 }, (_, i) => {
                const x = 32 + i * 3;
                const tx = 22 + i * 4.6;
                const ty = 16 + Math.abs(6 - i) * 2;
                return (
                    <g key={i}>
                        <path d={`M${x} 86 Q${(x + tx) / 2} 50 ${tx} ${ty}`} stroke={a.stem} strokeWidth="2" fill="none" />
                        <ellipse cx={tx} cy={ty - 1} rx="3" ry="2.4" fill={a.cap} stroke={shade(a.cap, -0.15)} strokeWidth="0.4" />
                    </g>
                );
            })}
            <rect x="30" y="80" width="42" height="9" rx="3" fill="#e6dccb" />
        </g>
    );
}

function Chaga({ a, uid, rnd }) {
    return (
        <g>
            <rect x="8" y="6" width="36" height="88" fill="#f2efe8" />
            {Array.from({ length: 9 }, (_, i) => <rect key={i} x={10 + rnd() * 26} y={10 + i * 9.5} width={6 + rnd() * 12} height="1.8" fill="#2b2622" opacity="0.8" />)}
            <path d="M38 30 C54 20 80 30 82 50 C84 70 66 82 44 76 C40 60 40 44 38 30Z" fill={`url(#${uid}-cap)`} />
            {Array.from({ length: 14 }, (_, i) => (
                <path key={i} d={`M${46 + rnd() * 30} ${34 + rnd() * 38} l${3 + rnd() * 4} ${rnd() * 3 - 1.5}`} stroke="#c07a2a" strokeWidth="0.9" opacity="0.8" />
            ))}
        </g>
    );
}

function Cluster({ a, uid, rnd }) {
    const n = a.n || 3;
    const items = Array.from({ length: n }, (_, i) => ({ cx: 50 + (i - (n - 1) / 2) * (60 / n), s: i === Math.floor(n / 2) ? 0.72 : 0.55 + rnd() * 0.1 }));
    return <g>{items.sort((x, y) => x.s - y.s).map((it, i) => <Mushroom key={i} uid={uid} a={a} cx={it.cx} scale={it.s} rnd={rnd} />)}</g>;
}

const MUSHROOM_DRAWERS = {
    cap: (p) => <Mushroom {...p} />,
    cluster: (p) => <Cluster {...p} />,
    funnel: (p) => <Funnel {...p} />,
    trumpet: (p) => <Funnel {...p} trumpet />,
    morel: (p) => <Morel {...p} />,
    truffle: (p) => <Truffle {...p} />,
    puffball: (p) => <Puffball {...p} />,
    oyster: (p) => <Oyster {...p} />,
    maned: (p) => <Maned {...p} />,
    coral: (p) => <Coral {...p} />,
    jelly: (p) => <Jelly {...p} />,
    inkcap: (p) => <Inkcap {...p} />,
    enoki: (p) => <Enoki {...p} />,
    chaga: (p) => <Chaga {...p} />,
};

// ================= PLANTS =================

function leafPath(shape, len, wid) {
    const l = len;
    const w = wid;
    switch (shape) {
        case 'needle': return `M0 0 L${l} ${-0.6} L${l} 0.6Z`;
        case 'round': return `M0 0 C${l * 0.1} ${-w} ${l} ${-w} ${l} 0 C${l} ${w} ${l * 0.1} ${w} 0 0Z`;
        case 'heart': return `M0 0 C${l * 0.2} ${-w * 1.1} ${l * 0.9} ${-w * 0.6} ${l} 0 C${l * 0.9} ${w * 0.6} ${l * 0.2} ${w * 1.1} 0 0Z`;
        case 'lance': return `M0 0 Q${l * 0.45} ${-w * 0.7} ${l} 0 Q${l * 0.45} ${w * 0.7} 0 0Z`;
        case 'small': return `M0 0 Q${l * 0.5} ${-w} ${l} 0 Q${l * 0.5} ${w} 0 0Z`;
        default: return `M0 0 C${l * 0.25} ${-w} ${l * 0.75} ${-w * 0.9} ${l} 0 C${l * 0.75} ${w * 0.9} ${l * 0.25} ${w} 0 0Z`; // ovate
    }
}

function Leaf({ uid, shape = 'ovate', len = 18, wid = 8, x, y, rot, color, serrated, fuzzy, veins = true }) {
    const d = leafPath(shape, len, wid);
    return (
        <g transform={`translate(${x} ${y}) rotate(${rot})`}>
            <path d={d} fill={`url(#${uid}-leaf)`} stroke={serrated ? shade(color, -0.3) : 'none'} strokeWidth={serrated ? 1.1 : 0} strokeDasharray={serrated ? '1.2 1' : undefined} />
            {veins && len > 8 && <path d={`M1 0 L${len * 0.92} 0`} stroke={shade(color, 0.35)} strokeWidth="0.6" opacity="0.7" />}
            {fuzzy && <path d={d} fill="#fff" opacity="0.22" />}
        </g>
    );
}

function Flowers({ at, a, rnd }) {
    const { flower, flowerType = 'cluster' } = a;
    if (!flower) return null;
    const [fx, fy] = at;
    if (flowerType === 'spike') {
        return <g>{Array.from({ length: 9 }, (_, i) => <circle key={i} cx={fx + (i % 2 ? 2 : -2)} cy={fy - i * 2.6} r={1.9 - i * 0.1} fill={flower} />)}</g>;
    }
    if (flowerType === 'ball') {
        return <g>{Array.from({ length: 22 }, (_, i) => { const ang = rnd() * Math.PI * 2; const r = rnd() * 6; return <circle key={i} cx={fx + Math.cos(ang) * r} cy={fy + Math.sin(ang) * r} r="1.8" fill={shade(flower, rnd() * 0.3 - 0.15)} />; })}</g>;
    }
    if (flowerType === 'star') {
        return <g>{[[-7, 2], [0, -4], [7, 2], [-3, 6], [4, 7]].map(([dx, dy], k) => (
            <g key={k} transform={`translate(${fx + dx} ${fy + dy})`}>{Array.from({ length: 6 }, (_, i) => <ellipse key={i} cx="0" cy="-2.2" rx="0.9" ry="2.2" fill={flower} transform={`rotate(${i * 60})`} />)}<circle r="0.8" fill="#e8d25a" /></g>
        ))}</g>;
    }
    return <g>{Array.from({ length: 7 }, (_, i) => <circle key={i} cx={fx - 6 + rnd() * 12} cy={fy - 4 + rnd() * 8} r="1.8" fill={flower} />)}</g>;
}

function Sprig({ uid, a, rnd }) {
    const pairs = a.pairs || 5;
    const stem = a.stemColor || shade(a.leaf, -0.25);
    const leaves = [];
    for (let i = 0; i < pairs; i++) {
        const y = 84 - i * (60 / pairs);
        const s = 1 - i * (0.5 / pairs);
        const len = (a.len || 20) * s;
        const wid = (a.wid || 8) * s;
        leaves.push(<Leaf key={`l${i}`} uid={uid} shape={a.shape} len={len} wid={wid} x={50} y={y} rot={180 + 25 + rnd() * 10} color={a.leaf} serrated={a.serrated} fuzzy={a.fuzzy} />);
        leaves.push(<Leaf key={`r${i}`} uid={uid} shape={a.shape} len={len} wid={wid} x={50} y={y - 2} rot={-25 - rnd() * 10} color={a.leaf} serrated={a.serrated} fuzzy={a.fuzzy} />);
    }
    return (
        <g>
            <path d={`M50 90 Q${48 + rnd() * 4} 55 50 ${a.flower ? 22 : 18}`} stroke={stem} strokeWidth={a.woody ? 2.4 : 1.8} fill="none" />
            {leaves}
            <Flowers at={[50, 18]} a={a} rnd={rnd} />
        </g>
    );
}

function Feather({ uid, a, rnd }) {
    const stem = shade(a.leaf, -0.2);
    const fronds = [];
    for (let i = 0; i < 5; i++) {
        const y = 82 - i * 12;
        const side = i % 2 ? 1 : -1;
        const len = 26 - i * 3;
        for (let j = 0; j < 7; j++) {
            const t = j / 6;
            const px = 50 + side * len * t;
            const py = y - len * t * 0.6;
            fronds.push(<path key={`${i}-${j}`} d={`M${px} ${py} l${side * 5} -4 M${px} ${py} l${side * 1} -6`} stroke={shade(a.leaf, rnd() * 0.2)} strokeWidth="1" strokeLinecap="round" />);
        }
        fronds.push(<path key={`s${i}`} d={`M50 ${y} q${side * len * 0.5} ${-len * 0.4} ${side * len} ${-len * 0.6}`} stroke={stem} strokeWidth="0.9" fill="none" />);
    }
    return (
        <g>
            <path d="M50 90 L50 18" stroke={stem} strokeWidth="1.6" />
            {fronds}
            {a.flower && <Umbel cx={50} cy={16} r={12} color={a.flower} rnd={rnd} />}
        </g>
    );
}

function Umbel({ cx, cy, r, color, rnd, berries }) {
    const rays = 9;
    return (
        <g>
            {Array.from({ length: rays }, (_, i) => {
                const ang = Math.PI + (i / (rays - 1)) * Math.PI;
                const x = cx + Math.cos(ang) * r;
                const y = cy + Math.sin(ang) * r * 0.45;
                return (
                    <g key={i}>
                        <path d={`M${cx} ${cy + 8} L${x} ${y}`} stroke="#6b7d4a" strokeWidth="0.6" />
                        {Array.from({ length: 5 }, (_, k) => (
                            <circle key={k} cx={x - 2 + rnd() * 4} cy={y - 1.5 + rnd() * 3} r={berries ? 1.6 : 1.2} fill={berries ? shade(color, rnd() * 0.2) : color} stroke={berries ? shade(color, -0.4) : 'none'} strokeWidth="0.3" />
                        ))}
                    </g>
                );
            })}
        </g>
    );
}

function UmbelPlant({ uid, a, rnd }) {
    return (
        <g>
            <path d="M50 90 L50 30" stroke={a.stemColor || shade(a.leaf, -0.25)} strokeWidth="2" />
            {a.stemSpots && Array.from({ length: 8 }, (_, i) => <circle key={i} cx={49.5 + rnd()} cy={40 + i * 6} r="0.8" fill="#8a2a3a" />)}
            <Leaf uid={uid} shape={a.shape || 'ovate'} len={24} wid={11} x={50} y={78} rot={200} color={a.leaf} serrated />
            <Leaf uid={uid} shape={a.shape || 'ovate'} len={22} wid={10} x={50} y={64} rot={-20} color={a.leaf} serrated />
            <Umbel cx={50} cy={24} r={22} color={a.flower} rnd={rnd} berries={a.berries} />
        </g>
    );
}

function Blades({ uid, a, rnd }) {
    const n = a.n || 7;
    const broad = a.broad;
    return (
        <g>
            {a.bulb && <ellipse cx="50" cy="80" rx="13" ry="11" fill={`url(#${uid}-fruit)`} />}
            {Array.from({ length: n }, (_, i) => {
                const t = i / (n - 1) - 0.5;
                const tipX = 50 + t * 50;
                const tipY = 12 + Math.abs(t) * 30 + rnd() * 6;
                const w = broad ? 7 : 1.8;
                return <path key={i} d={`M${50 + t * 6 - w / 2} 86 Q${50 + t * 30} ${50} ${tipX} ${tipY} Q${50 + t * 30 + w} ${52} ${50 + t * 6 + w / 2} 86Z`} fill={`url(#${uid}-leaf)`} />;
            })}
            {a.flower && <path d="M50 86 L50 22" stroke={shade(a.leaf, -0.2)} strokeWidth="1.3" />}
            <Flowers at={[50, 18]} a={a} rnd={rnd} />
        </g>
    );
}

function Rosette({ uid, a, rnd }) {
    const n = a.n || 9;
    return (
        <g>
            {Array.from({ length: n }, (_, i) => {
                const rot = -170 + (i / (n - 1)) * 160;
                return (
                    <g key={i}>
                        {a.stalk && <path d={`M50 86 L${50 + Math.cos((rot * Math.PI) / 180) * 18} ${86 + Math.sin((rot * Math.PI) / 180) * 18}`} stroke={a.stalk} strokeWidth="3" strokeLinecap="round" />}
                        <Leaf uid={uid} shape={a.shape || 'lance'} len={(a.len || 34) * (0.8 + rnd() * 0.3)} wid={a.wid || 9} x={50} y={86} rot={rot} color={a.leaf} serrated={a.serrated} />
                        {a.ribbed && <path d={`M50 86 l${Math.cos((rot * Math.PI) / 180) * 28} ${Math.sin((rot * Math.PI) / 180) * 28}`} stroke={shade(a.leaf, 0.3)} strokeWidth="0.5" opacity="0.8" />}
                    </g>
                );
            })}
            {a.flower && (
                <g>
                    <path d="M50 86 Q52 50 50 26" stroke={shade(a.leaf, -0.2)} strokeWidth="1.4" fill="none" />
                    <Flowers at={[50, 22]} a={a} rnd={rnd} />
                </g>
            )}
        </g>
    );
}

function FlowerHead({ a, uid, rnd }) {
    const heads = a.n || 1;
    const petals = a.petals || 14;
    const draw = (cx, cy, s, k) => (
        <g key={k} transform={`translate(${cx} ${cy}) scale(${s})`}>
            {Array.from({ length: petals }, (_, i) => (
                <ellipse key={i} cx="0" cy={a.thin ? -9 : -8} rx={a.thin ? 1.1 : petals <= 5 ? 5 : 2.6} ry={a.thin ? 8 : 7} fill={shade(a.flower, (i % 2) * -0.08)} transform={`rotate(${(360 / petals) * i})`} />
            ))}
            <circle r={a.center ? (a.thin ? 3 : 5) : 2} fill={`url(#${uid}-center)`} />
        </g>
    );
    return (
        <g>
            <path d="M50 90 Q46 60 50 40" stroke="#5f7d3a" strokeWidth="1.8" fill="none" />
            <Leaf uid={uid} shape={a.shape || 'lance'} len={18} wid={6} x={49} y={74} rot={200} color={a.leaf || '#5f8a3a'} serrated={a.serrated} />
            <Leaf uid={uid} shape={a.shape || 'lance'} len={16} wid={5} x={50} y={64} rot={-30} color={a.leaf || '#5f8a3a'} serrated={a.serrated} />
            {heads > 1 && <path d="M49 58 Q36 44 32 34 M51 56 Q62 44 68 36" stroke="#5f7d3a" strokeWidth="1.2" fill="none" />}
            {draw(50, 34, 1.3, 'm')}
            {heads > 1 && draw(32, 32, 0.8, 'a')}
            {heads > 2 && draw(68, 34, 0.75, 'b')}
        </g>
    );
}

function Berries({ uid, a, rnd }) {
    const shape = a.berryShape || 'round';
    const leaves = a.leafShape !== 'none';
    const cluster = a.hang
        ? Array.from({ length: a.n || 9 }, (_, i) => ({ x: 44 + (i % 2 ? 6 : -2) + i * 0.8, y: 30 + i * 5.5 }))
        : Array.from({ length: a.n || 6 }, (_, i) => ({ x: 32 + rnd() * 36, y: 46 + rnd() * 34 }));
    const berry = (b, i) => {
        const r = (a.size || 5) * (0.85 + rnd() * 0.3);
        if (shape === 'aggregate') {
            return <g key={i}>{Array.from({ length: 10 }, (_, k) => <circle key={k} cx={b.x + (k % 3 - 1) * r * 0.55} cy={b.y + (Math.floor(k / 3) - 1) * r * 0.55} r={r * 0.42} fill={`url(#${uid}-fruit)`} />)}</g>;
        }
        if (shape === 'hip') return <ellipse key={i} cx={b.x} cy={b.y} rx={r * 0.8} ry={r * 1.25} fill={`url(#${uid}-fruit)`} />;
        return (
            <g key={i}>
                <circle cx={b.x} cy={b.y} r={r} fill={`url(#${uid}-fruit)`} />
                {a.bloom && <circle cx={b.x} cy={b.y} r={r} fill="#cfd6ea" opacity="0.35" />}
                <circle cx={b.x - r * 0.35} cy={b.y - r * 0.4} r={r * 0.25} fill="#fff" opacity="0.5" />
            </g>
        );
    };
    return (
        <g>
            <path d={a.hang ? 'M30 90 Q40 40 46 22' : 'M50 90 Q46 60 50 30'} stroke={a.woody === false ? '#5f7d3a' : '#6d5237'} strokeWidth="2" fill="none" />
            {leaves && [0, 1, 2].map((i) => (
                <Leaf key={i} uid={uid} shape={a.leafShape || 'ovate'} len={20} wid={9} x={a.hang ? 40 + i * 2 : 50} y={74 - i * 16} rot={i % 2 ? -35 : 205} color={a.leaf || '#4f7a3a'} serrated={a.serrated} />
            ))}
            {a.thorns && Array.from({ length: 6 }, (_, i) => <path key={i} d={`M${48 + (i % 2) * 3} ${80 - i * 8} l${i % 2 ? 3 : -3} -1`} stroke="#6d5237" strokeWidth="1" />)}
            {cluster.map(berry)}
        </g>
    );
}

function Nut({ uid, a, rnd }) {
    return (
        <g>
            <Leaf uid={uid} shape="ovate" len={34} wid={14} x={30} y={30} rot={-10} color={a.leaf || '#5f8a3a'} serrated />
            {a.burr && (
                <g>
                    <circle cx="44" cy="62" r="18" fill="#9bb04a" />
                    {Array.from({ length: 40 }, (_, i) => { const ang = (i / 40) * Math.PI * 2; return <path key={i} d={`M${44 + Math.cos(ang) * 17} ${62 + Math.sin(ang) * 17} l${Math.cos(ang) * 6} ${Math.sin(ang) * 6}`} stroke="#b7c45a" strokeWidth="0.8" />; })}
                </g>
            )}
            <path d={a.walnut ? 'M52 58 C52 44 76 44 76 58 C76 74 52 74 52 58Z' : 'M56 50 C46 58 50 82 64 82 C78 82 80 58 68 50 C64 47 60 47 56 50Z'} fill={`url(#${uid}-fruit)`} />
            {a.walnut && <path d="M64 46 Q60 58 64 72" stroke={shade(a.fruit, -0.35)} strokeWidth="1" fill="none" />}
            {a.husk && <path d="M54 52 Q50 40 58 38 Q62 44 66 38 Q74 40 70 52 Q62 46 54 52Z" fill="#9bb04a" />}
        </g>
    );
}

function Conifer({ uid, a }) {
    return (
        <g>
            <path d="M20 30 L80 52" stroke="#6d5237" strokeWidth="2.4" />
            {Array.from({ length: 34 }, (_, i) => {
                const t = i / 33;
                const x = 22 + t * 56;
                const y = 30.5 + t * 21;
                return <path key={i} d={`M${x} ${y} l${i % 2 ? 4 : -2} ${i % 2 ? -12 : 12}`} stroke={shade(a.leaf, (i % 3) * 0.08)} strokeWidth="1.5" strokeLinecap="round" />;
            })}
            <g transform={a.spruce ? 'translate(52 58)' : 'translate(40 62)'}>
                <ellipse rx={a.spruce ? 7 : 9} ry={a.spruce ? 16 : 11} fill={`url(#${uid}-fruit)`} />
                {Array.from({ length: 12 }, (_, i) => <path key={i} d={`M-6 ${-10 + i * 2} q6 2 12 0`} stroke={shade(a.fruit, -0.35)} strokeWidth="0.7" fill="none" />)}
            </g>
        </g>
    );
}

function Root({ uid, a, rnd }) {
    const s = a.rootShape || 'round';
    const rootD = s === 'taper'
        ? 'M40 50 C40 44 60 44 60 50 C60 62 54 76 50 92 C46 76 40 62 40 50Z'
        : s === 'knobbly'
            ? 'M32 60 C28 44 44 40 50 42 C60 40 74 46 70 62 C72 76 58 82 50 80 C38 82 30 74 32 60Z'
            : 'M34 62 C34 46 66 46 66 62 C66 76 56 82 50 82 C44 82 34 76 34 62Z';
    return (
        <g>
            {Array.from({ length: 5 }, (_, i) => (
                <Leaf key={i} uid={uid} shape={a.leafShape || 'lance'} len={30} wid={a.leafShape === 'feather' ? 4 : 8} x={50} y={46} rot={-150 + i * 30} color={a.leaf || '#4f8a3a'} />
            ))}
            {a.stalk && Array.from({ length: 5 }, (_, i) => <path key={i} d={`M50 46 l${Math.cos(((-150 + i * 30) * Math.PI) / 180) * 10} ${Math.sin(((-150 + i * 30) * Math.PI) / 180) * 10}`} stroke={a.stalk} strokeWidth="2" />)}
            <path d={rootD} fill={`url(#${uid}-fruit)`} />
            {a.topColor && <path d="M36 54 C38 46 62 46 64 54 C58 52 42 52 36 54Z" fill={a.topColor} opacity="0.9" />}
            {Array.from({ length: 4 }, (_, i) => <path key={i} d={`M${40 + i * 5} ${58 + rnd() * 10} l6 1`} stroke={shade(a.fruit, -0.3)} strokeWidth="0.6" opacity="0.6" />)}
            {s !== 'taper' && <path d="M50 82 q1 5 -1 9" stroke={shade(a.fruit, -0.2)} strokeWidth="1" fill="none" />}
        </g>
    );
}

function Trefoil({ uid, a }) {
    return (
        <g>
            {[[36, 70], [60, 64]].map(([x, y], k) => (
                <g key={k}>
                    <path d={`M${x} 90 Q${x + 2} ${y + 10} ${x} ${y}`} stroke={shade(a.leaf, -0.2)} strokeWidth="1.2" fill="none" />
                    {[0, 120, 240].map((r) => <path key={r} d="M0 0 C-9 -4 -11 -16 -2 -16 C0 -15 0 -12 0 -12 C0 -12 0 -15 2 -16 C11 -16 9 -4 0 0Z" transform={`translate(${x} ${y}) rotate(${r})`} fill={`url(#${uid}-leaf)`} />)}
                </g>
            ))}
            {a.flower && <g transform="translate(70 30)">{Array.from({ length: 5 }, (_, i) => <ellipse key={i} cy="-4" rx="2.4" ry="4" fill={a.flower} transform={`rotate(${i * 72})`} />)}<circle r="1.3" fill="#e8d25a" /></g>}
        </g>
    );
}

function Cactus({ uid, a }) {
    return (
        <g>
            <ellipse cx="46" cy="66" rx="18" ry="24" fill={`url(#${uid}-leaf)`} />
            <ellipse cx="62" cy="38" rx="13" ry="17" fill={`url(#${uid}-leaf)`} transform="rotate(20 62 38)" />
            {Array.from({ length: 18 }, (_, i) => <circle key={i} cx={34 + (i % 5) * 6} cy={50 + Math.floor(i / 5) * 9} r="0.8" fill="#f2e6b0" />)}
            {[[56, 24], [70, 26], [74, 36]].map(([x, y], i) => <ellipse key={i} cx={x} cy={y} rx="5" ry="6.5" fill={`url(#${uid}-fruit)`} />)}
        </g>
    );
}

function Fruit({ uid, a }) {
    return (
        <g>
            <Leaf uid={uid} shape="ovate" len={30} wid={11} x={50} y={24} rot={-160} color={a.leaf || '#4f7a3a'} />
            <path d={a.oblong ? 'M36 40 C30 60 40 86 54 84 C68 82 70 58 62 40 C58 32 40 32 36 40Z' : 'M26 58 C26 40 74 40 74 58 C74 78 62 86 50 86 C38 86 26 78 26 58Z'} fill={`url(#${uid}-fruit)`} />
            {a.calyx && <path d="M40 44 L50 36 L60 44 L54 44 L50 40 L46 44Z" fill="#5f6a3a" />}
            <ellipse cx="40" cy="54" rx="6" ry="4" fill="#fff" opacity="0.3" />
        </g>
    );
}

function Stalks({ uid, a }) {
    return (
        <g>
            {[-24, -8, 8, 24].map((dx, i) => (
                <g key={i}>
                    <path d={`M50 90 Q${50 + dx * 0.4} 60 ${50 + dx} 40`} stroke={a.stalk} strokeWidth="4.5" strokeLinecap="round" fill="none" />
                    <Leaf uid={uid} shape="heart" len={26} wid={16} x={50 + dx} y={40} rot={-90 + dx * 2} color={a.leaf} />
                </g>
            ))}
        </g>
    );
}


// ================= GARDEN CROPS, TREES, HOUSEPLANTS, FLOWERS =================

/** A single fruit/vegetable body drawn at (x, y) with radius r. */
function FruitBody({ uid, shape = 'round', x, y, r, a, i = 0 }) {
    const fill = `url(#${uid}-fruit)`;
    const hi = <ellipse cx={x - r * 0.35} cy={y - r * 0.4} rx={r * 0.28} ry={r * 0.2} fill="#fff" opacity="0.45" />;
    switch (shape) {
        case 'ribbed': // beefsteak tomato
            return (
                <g>
                    <path d={`M${x - r} ${y} C${x - r} ${y - r * 1.05} ${x + r} ${y - r * 1.05} ${x + r} ${y} C${x + r} ${y + r * 0.9} ${x - r} ${y + r * 0.9} ${x - r} ${y}Z`} fill={fill} />
                    {[-0.45, 0, 0.45].map((k) => <path key={k} d={`M${x + k * r} ${y - r * 0.75} Q${x + k * r * 1.4} ${y} ${x + k * r} ${y + r * 0.7}`} stroke={shade(a.fruit, -0.25)} strokeWidth="0.7" fill="none" opacity="0.6" />)}
                    <path d={`M${x} ${y - r * 0.8} l-3 -2 l3 0.8 l0 -3 l1 3 l3 -1.5 l-2.5 2.5Z`} fill="#3f7a32" />
                    {hi}
                </g>
            );
        case 'oval': return <g><ellipse cx={x} cy={y} rx={r * 0.8} ry={r * 1.1} fill={fill} />{hi}</g>;
        case 'plum': return <g><ellipse cx={x} cy={y} rx={r * 0.85} ry={r} fill={fill} /><path d={`M${x} ${y - r} Q${x - r * 0.3} ${y} ${x} ${y + r}`} stroke={shade(a.fruit, -0.3)} strokeWidth="0.6" fill="none" />{a.bloom && <ellipse cx={x} cy={y} rx={r * 0.85} ry={r} fill="#cfd6ea" opacity="0.3" />}{hi}</g>;
        case 'bell':
            return (
                <g>
                    <path d={`M${x - r} ${y - r * 0.6} Q${x - r * 1.1} ${y + r} ${x - r * 0.5} ${y + r * 1.1} Q${x} ${y + r * 0.8} ${x + r * 0.5} ${y + r * 1.1} Q${x + r * 1.1} ${y + r} ${x + r} ${y - r * 0.6} Q${x} ${y - r * 1.1} ${x - r} ${y - r * 0.6}Z`} fill={fill} />
                    <path d={`M${x} ${y - r * 0.9} q1 -4 4 -5`} stroke="#3f7a32" strokeWidth="2" fill="none" strokeLinecap="round" />
                    {hi}
                </g>
            );
        case 'chili':
            return <g><path d={`M${x - r * 0.35} ${y - r} Q${x - r * 0.5} ${y + r * 1.2} ${x + r * 0.5} ${y + r * 2.4} Q${x + r * 0.15} ${y + r} ${x + r * 0.35} ${y - r}Z`} fill={fill} /><path d={`M${x} ${y - r} l0 -3`} stroke="#3f7a32" strokeWidth="2" /></g>;
        case 'cucumber':
            return (
                <g transform={`rotate(${a.tilt ?? 12} ${x} ${y})`}>
                    <rect x={x - r * 0.55} y={y - r * 1.9} width={r * 1.1} height={r * 3.8} rx={r * 0.55} fill={fill} />
                    {Array.from({ length: 8 }, (_, k) => <circle key={k} cx={x + (k % 2 ? 1 : -1) * r * 0.25} cy={y - r * 1.5 + k * r * 0.42} r="0.6" fill={shade(a.fruit, 0.4)} />)}
                    {a.stripes && Array.from({ length: 3 }, (_, k) => <path key={k} d={`M${x - r * 0.3 + k * r * 0.3} ${y - r * 1.7} l0 ${r * 3.4}`} stroke={shade(a.fruit, 0.25)} strokeWidth="0.8" opacity="0.6" />)}
                </g>
            );
        case 'eggplant':
            return <g transform={`rotate(-12 ${x} ${y})`}><path d={`M${x - r * 0.45} ${y - r * 0.9} C${x - r * 1.1} ${y + r * 0.3} ${x - r * 0.8} ${y + r * 1.8} ${x} ${y + r * 1.8} C${x + r * 0.8} ${y + r * 1.8} ${x + r * 1.1} ${y + r * 0.3} ${x + r * 0.45} ${y - r * 0.9}Z`} fill={fill} /><path d={`M${x - r * 0.6} ${y - r * 0.8} L${x} ${y - r * 1.3} L${x + r * 0.6} ${y - r * 0.8} L${x} ${y - r * 0.5}Z`} fill="#4f6a32" />{hi}</g>;
        case 'pear': return <g><path d={`M${x} ${y - r * 1.2} C${x - r * 0.5} ${y - r * 1.2} ${x - r * 0.4} ${y - r * 0.2} ${x - r * 0.9} ${y + r * 0.4} C${x - r * 1.1} ${y + r * 1.2} ${x + r * 1.1} ${y + r * 1.2} ${x + r * 0.9} ${y + r * 0.4} C${x + r * 0.4} ${y - r * 0.2} ${x + r * 0.5} ${y - r * 1.2} ${x} ${y - r * 1.2}Z`} fill={fill} /><path d={`M${x} ${y - r * 1.2} l1 -3`} stroke="#6d5237" strokeWidth="1.3" />{hi}</g>;
        case 'cherry': return <g><path d={`M${x - r * 0.6} ${y} Q${x - r * 0.2} ${y - r * 2} ${x + r * 0.4} ${y - r * 2.4} M${x + r * 0.7} ${y + r * 0.2} Q${x + r * 0.5} ${y - r * 1.6} ${x + r * 0.4} ${y - r * 2.4}`} stroke="#5a7a32" strokeWidth="0.9" fill="none" /><circle cx={x - r * 0.6} cy={y + r * 0.4} r={r * 0.7} fill={fill} /><circle cx={x + r * 0.7} cy={y + r * 0.6} r={r * 0.7} fill={fill} /></g>;
        case 'citrus': return <g><circle cx={x} cy={y} r={r} fill={fill} />{Array.from({ length: 10 }, (_, k) => <circle key={k} cx={x + Math.cos(k * 2.3) * r * 0.6} cy={y + Math.sin(k * 2.3) * r * 0.6} r="0.5" fill={shade(a.fruit, -0.2)} opacity="0.6" />)}<circle cx={x} cy={y - r * 0.9} r="1" fill="#5a7a32" />{hi}</g>;
        case 'pod': return <g transform={`rotate(${20 - i * 12} ${x} ${y})`}><path d={`M${x - r * 0.4} ${y - r * 2} Q${x - r * 0.6} ${y} ${x} ${y + r * 2} Q${x + r * 0.6} ${y} ${x + r * 0.4} ${y - r * 2}Z`} fill={fill} />{a.peas && [-1, 0, 1].map((k) => <circle key={k} cx={x} cy={y + k * r * 0.8} r={r * 0.3} fill={shade(a.fruit, 0.25)} />)}</g>;
        case 'long': return <g transform={`rotate(${i * 8 - 8} ${x} ${y})`}><path d={`M${x - r * 0.18} ${y - r * 1.6} Q${x - r * 0.3} ${y + r} ${x + r * 0.1} ${y + r * 2.6} Q${x + r * 0.3} ${y + r} ${x + r * 0.18} ${y - r * 1.6}Z`} fill={fill} /></g>;
        case 'strawberry': return <g><path d={`M${x - r} ${y - r * 0.4} Q${x - r} ${y + r * 0.8} ${x} ${y + r * 1.3} Q${x + r} ${y + r * 0.8} ${x + r} ${y - r * 0.4} Q${x} ${y - r * 0.8} ${x - r} ${y - r * 0.4}Z`} fill={fill} />{Array.from({ length: 9 }, (_, k) => <circle key={k} cx={x - r * 0.6 + (k % 3) * r * 0.6} cy={y - r * 0.1 + Math.floor(k / 3) * r * 0.45} r="0.55" fill="#f7e27a" />)}<path d={`M${x - r * 0.7} ${y - r * 0.5} L${x} ${y - r * 0.9} L${x + r * 0.7} ${y - r * 0.5} L${x} ${y - r * 0.2}Z`} fill="#3f8a32" /></g>;
        case 'kiwi': return <g><ellipse cx={x} cy={y} rx={r} ry={r * 0.8} fill={fill} /><ellipse cx={x + r * 0.5} cy={y + r * 0.2} rx={r * 0.55} ry={r * 0.5} fill="#8ac43a" stroke="#6d5237" strokeWidth="1" /><circle cx={x + r * 0.5} cy={y + r * 0.2} r={r * 0.15} fill="#f2f0c8" /></g>;
        case 'dragon': return <g><ellipse cx={x} cy={y} rx={r * 0.85} ry={r * 1.1} fill={fill} />{Array.from({ length: 7 }, (_, k) => <path key={k} d={`M${x - r * 0.6 + (k % 3) * r * 0.6} ${y - r * 0.7 + Math.floor(k / 3) * r * 0.6} q2 -4 5 -4`} stroke="#7ac43a" strokeWidth="1.6" fill="none" />)}</g>;
        case 'pomegranate': return <g><circle cx={x} cy={y} r={r} fill={fill} /><path d={`M${x - r * 0.3} ${y - r * 0.9} l-1 -4 l2 2 l0.8 -3 l0.8 3 l2 -2 l-1 4Z`} fill={shade(a.fruit, -0.2)} />{hi}</g>;
        case 'avocado': return <g><path d={`M${x} ${y - r * 1.3} C${x - r * 0.5} ${y - r * 1.3} ${x - r} ${y + r * 0.2} ${x - r * 0.9} ${y + r * 0.6} C${x - r * 0.8} ${y + r * 1.3} ${x + r * 0.8} ${y + r * 1.3} ${x + r * 0.9} ${y + r * 0.6} C${x + r} ${y + r * 0.2} ${x + r * 0.5} ${y - r * 1.3} ${x} ${y - r * 1.3}Z`} fill={fill} />{a.cut && <><path d={`M${x} ${y - r} C${x - r * 0.4} ${y - r} ${x - r * 0.7} ${y + r * 0.2} ${x - r * 0.65} ${y + r * 0.5} C${x - r * 0.55} ${y + r} ${x + r * 0.55} ${y + r} ${x + r * 0.65} ${y + r * 0.5} C${x + r * 0.7} ${y + r * 0.2} ${x + r * 0.4} ${y - r} ${x} ${y - r}Z`} fill="#d8e89a" /><circle cx={x} cy={y + r * 0.35} r={r * 0.38} fill="#8a5a2a" /></>}</g>;
        case 'banana': return <g>{[0, 1, 2, 3].map((k) => <path key={k} d={`M${x - r} ${y - r + k * 3} Q${x + r * 0.2} ${y + r * 1.4 + k * 2} ${x + r * 1.5} ${y - r * 0.2 + k * 4} Q${x + r * 0.2} ${y + r * 0.8 + k * 2} ${x - r} ${y - r + k * 3}Z`} fill={shade(a.fruit, k * -0.06)} stroke={shade(a.fruit, -0.3)} strokeWidth="0.4" />)}<rect x={x - r - 3} y={y - r - 2} width="4" height="8" fill="#6d5237" /></g>;
        case 'pineapple': return <g><ellipse cx={x} cy={y + r * 0.4} rx={r * 0.8} ry={r * 1.1} fill={fill} />{Array.from({ length: 12 }, (_, k) => <path key={k} d={`M${x - r * 0.6 + (k % 4) * r * 0.4} ${y - r * 0.3 + Math.floor(k / 4) * r * 0.55} l${r * 0.2} ${r * 0.2} l${r * 0.2} ${-r * 0.2}`} stroke={shade(a.fruit, -0.35)} strokeWidth="0.7" fill="none" />)}{[-40, -20, 0, 20, 40].map((d) => <path key={d} d={`M${x} ${y - r * 0.6} l0 -${r * 1.2}`} stroke="#4f8a3a" strokeWidth="2.4" strokeLinecap="round" transform={`rotate(${d} ${x} ${y - r * 0.6})`} />)}</g>;
        case 'fig': return <g><path d={`M${x} ${y - r * 1.2} C${x - r * 0.3} ${y - r * 0.6} ${x - r * 1.1} ${y - r * 0.2} ${x - r} ${y + r * 0.5} C${x - r * 0.8} ${y + r * 1.2} ${x + r * 0.8} ${y + r * 1.2} ${x + r} ${y + r * 0.5} C${x + r * 1.1} ${y - r * 0.2} ${x + r * 0.3} ${y - r * 0.6} ${x} ${y - r * 1.2}Z`} fill={fill} />{hi}</g>;
        case 'passion': return <g><circle cx={x} cy={y} r={r} fill={fill} />{Array.from({ length: 6 }, (_, k) => <circle key={k} cx={x + Math.cos(k) * r * 0.5} cy={y + Math.sin(k * 1.7) * r * 0.5} r="0.8" fill={shade(a.fruit, 0.3)} opacity="0.6" />)}{hi}</g>;
        default: return <g><circle cx={x} cy={y} r={r} fill={fill} />{a.calyx !== false && <path d={`M${x - 2.5} ${y - r + 0.5} L${x} ${y - r - 2} L${x + 2.5} ${y - r + 0.5}`} stroke="#3f7a32" strokeWidth="1.2" fill="none" />}{hi}</g>;
    }
}

function Crop({ uid, a, rnd }) {
    const n = a.n || 3;
    const size = a.size || 9;
    const vine = a.vine !== false;
    const spots = a.truss
        ? Array.from({ length: n }, (_, i) => ({ x: 44 + (i % 2) * 9 + i * 1.5, y: 30 + i * (46 / n) }))
        : Array.from({ length: n }, (_, i) => ({ x: 50 + (i - (n - 1) / 2) * (size * 1.9), y: 50 + (i % 2) * size * 1.4 + rnd() * 4 }));
    return (
        <g>
            {vine && <path d={a.truss ? 'M38 90 Q40 50 46 22' : 'M50 90 Q46 60 50 26'} stroke={shade(a.leaf || '#4f7a3a', -0.25)} strokeWidth="2.2" fill="none" />}
            {vine && [0, 1, 2].map((i) => (
                <Leaf key={i} uid={uid} shape={a.leafShape || 'ovate'} len={a.leafLen || 20} wid={a.leafWid || 9} x={a.truss ? 40 + i * 2 : 50} y={80 - i * 20} rot={i % 2 ? -30 : 210} color={a.leaf || '#4f7a3a'} serrated={a.serrated} />
            ))}
            {a.truss && spots.map((s, i) => <path key={`t${i}`} d={`M${a.truss ? 44 : 50} ${s.y - 6} L${s.x} ${s.y - size}`} stroke="#5a7a32" strokeWidth="0.8" />)}
            {spots.map((s, i) => <FruitBody key={i} uid={uid} shape={a.shape} x={s.x} y={s.y} r={size} a={a} i={i} />)}
        </g>
    );
}

function FruitTree({ uid, a, rnd }) {
    const n = a.n || 7;
    return (
        <g>
            <path d="M46 90 L47 58 Q40 50 36 44 M47 62 Q56 52 62 46 M48 90 L53 90 L52 58" stroke="#6d5237" strokeWidth="4" fill="#6d5237" strokeLinecap="round" />
            <ellipse cx="50" cy="38" rx="34" ry="26" fill={`url(#${uid}-leaf)`} />
            {Array.from({ length: 9 }, (_, i) => <circle key={i} cx={24 + rnd() * 52} cy={20 + rnd() * 34} r={5 + rnd() * 5} fill={shade(a.leaf || '#4f8a3a', rnd() * 0.25 - 0.1)} opacity="0.8" />)}
            {Array.from({ length: n }, (_, i) => <FruitBody key={i} uid={uid} shape={a.shape || 'round'} x={24 + rnd() * 52} y={26 + rnd() * 26} r={a.size || 4} a={{ ...a, calyx: false }} i={i} />)}
        </g>
    );
}

function Pot({ color = '#c0673e' }) {
    return (
        <g>
            <path d="M30 70 L70 70 L66 92 L34 92Z" fill={color} />
            <rect x="27" y="66" width="46" height="7" rx="2" fill={shade(color, -0.1)} />
            <path d="M34 73 L36 90" stroke="#fff" strokeWidth="1.5" opacity="0.25" />
        </g>
    );
}

function Potted({ uid, a, rnd }) {
    const leaf = a.leaf || '#3f7a3a';
    const L = (props) => <Leaf uid={uid} color={leaf} {...props} />;
    let body;
    switch (a.plant) {
        case 'monstera':
            body = [[-40, 30], [-5, 32], [30, 30], [-70, 22], [60, 22]].map(([rot, len], i) => (
                <g key={i}>
                    <L shape="heart" len={len} wid={17} x={50} y={68} rot={rot - 90} />
                    {[0.35, 0.55, 0.75].map((k) => <circle key={k} cx={50 + Math.cos(((rot - 90) * Math.PI) / 180) * len * k + 5} cy={68 + Math.sin(((rot - 90) * Math.PI) / 180) * len * k} r="2" fill={`url(#${uid}-bgp)`} />)}
                </g>
            ));
            break;
        case 'snake':
            body = [-14, -6, 2, 10, 16].map((dx, i) => <path key={i} d={`M${50 + dx} 70 Q${48 + dx * 1.2} 40 ${50 + dx * 1.4} ${14 + (i % 2) * 8} Q${54 + dx * 1.2} 40 ${54 + dx} 70Z`} fill={`url(#${uid}-leaf)`} stroke="#c8d86a" strokeWidth="1" />);
            break;
        case 'pothos':
            body = [...Array(9)].map((_, i) => <L key={i} shape="heart" len={11} wid={7} x={34 + (i % 3) * 14} y={66 + Math.floor(i / 3) * 9} rot={70 + rnd() * 40} />);
            body.push(<path key="v" d="M36 70 Q24 84 30 96 M64 70 Q76 84 70 96" stroke={shade(leaf, -0.2)} strokeWidth="1.2" fill="none" />);
            break;
        case 'succulent':
            body = Array.from({ length: 14 }, (_, i) => <ellipse key={i} cx={50} cy={62 - (i % 7) * 0.8} rx={4 + (i % 7) * 0.4} ry={10 - (i % 7)} fill={`url(#${uid}-leaf)`} transform={`rotate(${i * 26} 50 64)`} opacity="0.95" />);
            break;
        case 'cactus':
            body = (
                <g>
                    <rect x="42" y="22" width="16" height="50" rx="8" fill={`url(#${uid}-leaf)`} />
                    <path d="M42 48 Q30 48 30 36 L30 30" stroke={leaf} strokeWidth="8" strokeLinecap="round" fill="none" />
                    <path d="M58 42 Q68 42 68 32 L68 28" stroke={leaf} strokeWidth="8" strokeLinecap="round" fill="none" />
                    {Array.from({ length: 14 }, (_, i) => <path key={i} d={`M${45 + (i % 3) * 5} ${28 + Math.floor(i / 3) * 9} l2 -1.5`} stroke="#f2e6c0" strokeWidth="0.8" />)}
                    {a.flower && <circle cx="50" cy="21" r="4" fill={a.flower} />}
                </g>
            );
            break;
        case 'fern':
            body = [-60, -35, -12, 12, 35, 60].map((rot, i) => (
                <g key={i} transform={`rotate(${rot} 50 68)`}>
                    <path d="M50 68 Q50 44 50 24" stroke={shade(leaf, -0.2)} strokeWidth="1" fill="none" />
                    {Array.from({ length: 9 }, (_, k) => <path key={k} d={`M50 ${66 - k * 4.6} l${k % 2 ? 6 - k * 0.4 : -(6 - k * 0.4)} -2`} stroke={leaf} strokeWidth="2.2" strokeLinecap="round" />)}
                </g>
            ));
            break;
        case 'palm':
            body = [-65, -35, -8, 18, 45, 70].map((rot, i) => (
                <g key={i} transform={`rotate(${rot} 50 60)`}>
                    <path d="M50 70 Q52 40 50 16" stroke={shade(leaf, -0.25)} strokeWidth="1.2" fill="none" />
                    {Array.from({ length: 8 }, (_, k) => <path key={k} d={`M50 ${60 - k * 5} l${k % 2 ? 9 : -9} 4`} stroke={leaf} strokeWidth="1.8" strokeLinecap="round" />)}
                </g>
            ));
            break;
        case 'orchid':
            body = (
                <g>
                    <L shape="ovate" len={20} wid={8} x={50} y={68} rot={200} />
                    <L shape="ovate" len={20} wid={8} x={50} y={68} rot={-20} />
                    <path d="M52 68 Q60 36 44 20" stroke="#6a7a4a" strokeWidth="1.2" fill="none" />
                    {[[56, 40], [52, 30], [45, 22]].map(([x, y], i) => (
                        <g key={i} transform={`translate(${x} ${y})`}>
                            {[0, 72, 144, 216, 288].map((r) => <ellipse key={r} cy="-4" rx="3.2" ry="4.5" fill={a.flower || '#f4c6e4'} transform={`rotate(${r})`} />)}
                            <circle r="1.6" fill={a.center || '#e8568a'} />
                        </g>
                    ))}
                </g>
            );
            break;
        case 'lily': // peace lily / anthurium
            body = (
                <g>
                    {[-50, -20, 10, 40].map((rot, i) => <L key={i} shape="lance" len={28} wid={9} x={50} y={68} rot={rot - 90} />)}
                    <path d="M50 68 L50 26" stroke={shade(leaf, -0.2)} strokeWidth="1.2" />
                    <path d={a.plant2 === 'anthurium' ? 'M50 26 C38 18 40 34 50 38 C60 34 62 18 50 26Z' : 'M50 22 C42 26 42 40 50 44 C58 40 58 26 50 22Z'} fill={a.flower || '#f7f7f2'} />
                    <path d="M50 30 L51 42" stroke={a.center || '#f2e27a'} strokeWidth="2.5" strokeLinecap="round" />
                </g>
            );
            break;
        case 'broad': // rubber plant / fiddle-leaf fig / alocasia / calathea
            body = (
                <g>
                    <path d="M50 70 L50 20" stroke="#6d5237" strokeWidth="2" />
                    {Array.from({ length: 6 }, (_, i) => <L key={i} shape={a.leafShape || 'ovate'} len={a.leafLen || 20} wid={a.leafWid || 11} x={50} y={64 - i * 8} rot={i % 2 ? -30 - i * 3 : 210 + i * 3} />)}
                    {a.stripes && Array.from({ length: 6 }, (_, i) => <path key={i} d={`M${50 + (i % 2 ? 4 : -4)} ${62 - i * 8} l${i % 2 ? 10 : -10} -4`} stroke={a.stripes} strokeWidth="1.2" opacity="0.8" />)}
                </g>
            );
            break;
        case 'spider':
            body = Array.from({ length: 12 }, (_, i) => {
                const rot = -170 + i * 15;
                return <g key={i}><path d={`M50 68 Q${50 + Math.cos((rot * Math.PI) / 180) * 18} ${68 + Math.sin((rot * Math.PI) / 180) * 24} ${50 + Math.cos((rot * Math.PI) / 180) * 30} ${72 + Math.sin((rot * Math.PI) / 180) * 18 + (Math.abs(rot + 90) > 60 ? 16 : 0)}`} stroke={leaf} strokeWidth="2.6" fill="none" strokeLinecap="round" /><path d={`M50 68 Q${50 + Math.cos((rot * Math.PI) / 180) * 18} ${68 + Math.sin((rot * Math.PI) / 180) * 24} ${50 + Math.cos((rot * Math.PI) / 180) * 30} ${72 + Math.sin((rot * Math.PI) / 180) * 18 + (Math.abs(rot + 90) > 60 ? 16 : 0)}`} stroke="#f2f6e0" strokeWidth="0.8" fill="none" /></g>;
            });
            break;
        case 'jade':
            body = (
                <g>
                    <path d="M50 70 L50 40 M50 52 L38 38 M50 48 L62 34" stroke="#7a6a4a" strokeWidth="3.5" strokeLinecap="round" />
                    {[[38, 34], [34, 40], [62, 30], [66, 36], [50, 34], [46, 38], [56, 38]].map(([x, y], i) => <ellipse key={i} cx={x} cy={y} rx="5" ry="3.6" fill={`url(#${uid}-leaf)`} stroke="#b8403a" strokeWidth="0.5" />)}
                </g>
            );
            break;
        case 'pilea':
            body = Array.from({ length: 8 }, (_, i) => {
                const rot = -160 + i * 20;
                const x = 50 + Math.cos((rot * Math.PI) / 180) * 20;
                const y = 62 + Math.sin((rot * Math.PI) / 180) * 26;
                return <g key={i}><path d={`M50 68 L${x} ${y}`} stroke={shade(leaf, -0.2)} strokeWidth="0.9" /><circle cx={x} cy={y} r="7" fill={`url(#${uid}-leaf)`} /><circle cx={x} cy={y} r="1" fill={shade(leaf, 0.4)} /></g>;
            });
            break;
        case 'flytrap':
            body = [-60, -20, 20, 60].map((rot, i) => (
                <g key={i} transform={`rotate(${rot} 50 68)`}>
                    <path d="M50 68 L50 46" stroke={leaf} strokeWidth="2.5" />
                    <path d="M42 44 Q50 30 58 44Z" fill="#c8303a" stroke={leaf} strokeWidth="1.2" />
                    {Array.from({ length: 6 }, (_, k) => <path key={k} d={`M${43 + k * 3} 44 l0 3`} stroke={leaf} strokeWidth="0.7" />)}
                </g>
            ));
            break;
        case 'aloe':
            body = [-60, -35, -12, 12, 35, 60].map((rot, i) => <path key={i} d="M46 70 Q48 40 50 18 Q52 40 54 70Z" fill={`url(#${uid}-leaf)`} transform={`rotate(${rot} 50 70)`} stroke="#e8f0d8" strokeWidth="0.6" strokeDasharray="1 2" />);
            break;
        case 'bloomer': // poinsettia, begonia, amaryllis
            body = (
                <g>
                    {[-50, -20, 20, 50].map((rot, i) => <L key={i} shape="ovate" len={20} wid={10} x={50} y={68} rot={rot - 90} serrated={a.serrated} />)}
                    {a.star
                        ? Array.from({ length: 7 }, (_, i) => <L key={`b${i}`} shape="lance" len={16} wid={7} x={50} y={36} rot={i * 51} color={a.flower} />)
                        : <path d="M50 68 L50 30" stroke={shade(leaf, -0.2)} strokeWidth="2" />}
                    {!a.star && [0, 60, 120, 180, 240, 300].map((r) => <ellipse key={r} cx="50" cy="26" rx="3.5" ry="9" fill={a.flower} transform={`rotate(${r} 50 30)`} />)}
                    <circle cx="50" cy={a.star ? 36 : 30} r="2.2" fill={a.center || '#f2d23a'} />
                </g>
            );
            break;
        default: // generic leafy houseplant
            body = [-60, -30, 0, 30, 60].map((rot, i) => <L key={i} shape={a.leafShape || 'ovate'} len={a.leafLen || 24} wid={a.leafWid || 11} x={50} y={68} rot={rot - 90} />);
    }
    return (
        <g>
            <defs><radialGradient id={`${uid}-bgp`}><stop offset="0" stopColor="#f4efe4" /><stop offset="1" stopColor="#e8e0d0" /></radialGradient></defs>
            {body}
            <Pot color={a.pot} />
        </g>
    );
}

function Bloom({ uid, a, rnd }) {
    const f = a.flower || '#e0445a';
    let head;
    switch (a.type) {
        case 'tulip': head = <path d="M40 30 Q40 14 44 12 L47 20 L50 10 L53 20 L56 12 Q60 14 60 30 Q60 40 50 40 Q40 40 40 30Z" fill={`url(#${uid}-center)`} />; break;
        case 'rose':
            head = <g>{[14, 11, 8, 5].map((r, i) => <circle key={r} cx="50" cy="26" r={r} fill={shade(f, i * 0.08 - 0.1)} stroke={shade(f, -0.35)} strokeWidth="0.6" />)}<path d="M46 24 Q50 20 54 24 Q50 30 46 24" stroke={shade(f, -0.4)} strokeWidth="0.8" fill="none" /></g>;
            break;
        case 'daffodil':
            head = <g transform="translate(50 26)">{[0, 60, 120, 180, 240, 300].map((r) => <ellipse key={r} cy="-8" rx="4.5" ry="8" fill={f} transform={`rotate(${r})`} />)}<circle r="6" fill={a.center || '#f29a1a'} /><circle r="3.5" fill={shade(a.center || '#f29a1a', -0.2)} /></g>;
            break;
        case 'poppy':
            head = <g transform="translate(50 26)">{[0, 90, 180, 270].map((r) => <circle key={r} cx="0" cy="-7" r="9" fill={f} transform={`rotate(${r})`} opacity="0.95" />)}<circle r="4" fill="#2a2320" /></g>;
            break;
        case 'spike':
            head = <g>{Array.from({ length: 12 }, (_, i) => <ellipse key={i} cx={50 + (i % 2 ? 3 : -3)} cy={44 - i * 3} rx={3.4 - i * 0.15} ry="2.2" fill={shade(f, (i % 3) * 0.08)} />)}</g>;
            break;
        case 'ball':
            head = <g>{Array.from({ length: 30 }, (_, i) => { const ang = rnd() * Math.PI * 2; const r = rnd() * 13; return <circle key={i} cx={50 + Math.cos(ang) * r} cy={28 + Math.sin(ang) * r} r="3" fill={shade(f, rnd() * 0.3 - 0.1)} />; })}</g>;
            break;
        case 'trumpet':
            head = <g>{[-35, 0, 35].map((r) => <path key={r} d="M50 34 L40 14 Q50 8 60 14Z" fill={f} transform={`rotate(${r} 50 34)`} stroke={shade(f, -0.2)} strokeWidth="0.6" />)}<path d="M50 34 l-2 -12 M50 34 l2 -12" stroke={a.center || '#8a3a2a'} strokeWidth="0.8" /></g>;
            break;
        case 'iris':
            head = <g><path d="M50 30 Q40 20 44 10 Q50 16 50 30 Q50 16 56 10 Q60 20 50 30Z" fill={f} /><path d="M50 30 Q36 30 36 42 Q46 38 50 30 Q54 38 64 42 Q64 30 50 30Z" fill={shade(f, -0.15)} /><path d="M50 30 l0 6" stroke={a.center || '#f2d23a'} strokeWidth="2" /></g>;
            break;
        case 'pansy':
            head = <g transform="translate(50 28)">{[[-6, -6, 8], [6, -6, 8], [-7, 4, 7], [7, 4, 7], [0, 7, 7]].map(([x, y, r], i) => <circle key={i} cx={x} cy={y} r={r} fill={i < 2 ? shade(f, 0.15) : f} />)}<circle r="2.5" fill={a.center || '#f2d23a'} /><path d="M0 2 l-5 5 M0 2 l5 5 M0 2 l0 7" stroke="#2a1a3a" strokeWidth="0.8" /></g>;
            break;
        case 'cup': // hollyhock, clematis, cosmos
            head = <g transform="translate(50 26)">{[0, 72, 144, 216, 288].map((r) => <ellipse key={r} cy="-8" rx="7" ry="9" fill={f} transform={`rotate(${r})`} stroke={shade(f, -0.2)} strokeWidth="0.5" />)}<circle r="4" fill={a.center || '#f2d23a'} /></g>;
            break;
        default:
            return <FlowerHead uid={uid} a={a} rnd={rnd} />;
    }
    return (
        <g>
            <path d="M50 90 Q48 64 50 36" stroke="#4f7a32" strokeWidth="2" fill="none" />
            <Leaf uid={uid} shape={a.leafShape || 'lance'} len={22} wid={6} x={50} y={78} rot={210} color={a.leaf || '#4f8a3a'} />
            <Leaf uid={uid} shape={a.leafShape || 'lance'} len={20} wid={6} x={50} y={68} rot={-35} color={a.leaf || '#4f8a3a'} />
            {head}
        </g>
    );
}

function Head({ uid, a }) {
    const leaf = a.leaf || '#6aa84a';
    return (
        <g>
            {[-70, -35, 0, 35, 70].map((r, i) => <Leaf key={i} uid={uid} shape="round" len={30} wid={18} x={50} y={72} rot={r - 90} color={shade(leaf, -0.1)} serrated={a.ruffled} />)}
            <circle cx="50" cy="60" r={a.small ? 13 : 19} fill={`url(#${uid}-fruit)`} />
            {!a.loose && [0.55, 0.8].map((k) => <path key={k} d={`M${50 - 19 * k} 60 Q50 ${60 - 24 * k} ${50 + 19 * k} 60`} stroke={shade(a.fruit, -0.25)} strokeWidth="0.8" fill="none" />)}
            {a.ruffled && <path d="M34 58 q4 -4 8 0 t8 0 t8 0 t8 0" stroke={shade(a.fruit, 0.25)} strokeWidth="1.4" fill="none" />}
        </g>
    );
}

function Bulb({ uid, a }) {
    const garlic = a.shape === 'garlic';
    return (
        <g>
            {[-20, -8, 4, 16].map((dx, i) => <path key={i} d={`M${48 + dx * 0.15} 56 Q${50 + dx * 0.6} 30 ${50 + dx} ${12 + (i % 2) * 6}`} stroke={a.leaf || '#4f8a3a'} strokeWidth={a.fennel ? 1 : 3} fill="none" strokeLinecap="round" />)}
            {a.fennel && Array.from({ length: 16 }, (_, i) => <path key={i} d={`M${40 + (i % 8) * 3} ${16 + Math.floor(i / 8) * 8} l2 -4`} stroke={a.leaf} strokeWidth="0.8" />)}
            <path d={garlic ? 'M32 70 C30 56 42 50 50 50 C58 50 70 56 68 70 C68 82 58 86 50 86 C42 86 32 82 32 70Z' : a.shape === 'shallot' ? 'M38 72 C36 58 46 50 50 50 C54 50 64 58 62 72 C62 82 56 86 50 86 C44 86 38 82 38 72Z' : 'M30 70 C30 56 44 52 50 50 C56 52 70 56 70 70 C70 82 60 88 50 88 C40 88 30 82 30 70Z'} fill={`url(#${uid}-fruit)`} />
            {garlic && [40, 50, 60].map((x) => <path key={x} d={`M${x} 54 Q${x - 3} 70 ${x} 86`} stroke={shade(a.fruit, -0.2)} strokeWidth="0.7" fill="none" />)}
            {!garlic && [42, 58].map((x) => <path key={x} d={`M${x} 56 Q${x + (x < 50 ? -6 : 6)} 70 ${x} 86`} stroke={shade(a.fruit, -0.25)} strokeWidth="0.6" fill="none" />)}
            <path d="M46 88 q4 4 8 0" stroke="#c8b89a" strokeWidth="0.8" fill="none" />
        </g>
    );
}

function Tuber({ uid, a, rnd }) {
    const shapes = { potato: [[34, 76, 12, 9], [58, 80, 10, 8], [48, 66, 8, 6]], sweet: [[40, 74, 16, 7], [62, 80, 12, 6]], ginger: [[36, 74, 9, 6], [48, 70, 8, 5], [60, 75, 10, 6], [52, 80, 7, 5]], jerusalem: [[40, 74, 9, 8], [56, 78, 9, 7], [50, 66, 7, 6]] };
    return (
        <g>
            <path d="M50 64 L50 20" stroke={shade(a.leaf || '#4f7a3a', -0.2)} strokeWidth="2" />
            {[0, 1, 2, 3].map((i) => <Leaf key={i} uid={uid} shape={a.leafShape || 'ovate'} len={18} wid={a.leafShape === 'lance' ? 4 : 9} x={50} y={60 - i * 11} rot={i % 2 ? -35 : 215} color={a.leaf || '#4f7a3a'} />)}
            {a.flower && <Flowers at={[50, 18]} a={a} rnd={rnd} />}
            <path d="M18 68 L82 68" stroke="#8a6a4a" strokeWidth="1" opacity="0.4" />
            {(shapes[a.shape] || shapes.potato).map(([x, y, rx, ry], i) => (
                <g key={i}>
                    <ellipse cx={x} cy={y} rx={rx} ry={ry} fill={`url(#${uid}-fruit)`} transform={`rotate(${a.shape === 'sweet' ? 15 : 0} ${x} ${y})`} />
                    {[0, 1].map((k) => <circle key={k} cx={x - rx * 0.4 + k * rx * 0.7} cy={y - ry * 0.2 + k * 2} r="0.8" fill={shade(a.fruit, -0.35)} />)}
                </g>
            ))}
        </g>
    );
}

function Gourd({ uid, a }) {
    const shape = a.shape || 'pumpkin';
    let body;
    if (shape === 'butternut') body = <path d="M50 20 C44 20 44 40 40 52 C30 64 36 88 50 88 C64 88 70 64 60 52 C56 40 56 20 50 20Z" fill={`url(#${uid}-fruit)`} />;
    else if (shape === 'pattypan') body = <path d="M18 64 Q22 54 30 58 Q34 48 42 54 Q50 44 58 54 Q66 48 70 58 Q78 54 82 64 Q78 78 50 80 Q22 78 18 64Z" fill={`url(#${uid}-fruit)`} />;
    else if (shape === 'melon' || shape === 'watermelon') body = <ellipse cx="50" cy="62" rx={shape === 'watermelon' ? 32 : 26} ry={shape === 'watermelon' ? 24 : 24} fill={`url(#${uid}-fruit)`} />;
    else body = <g>{[-18, -6, 6, 18].map((dx) => <ellipse key={dx} cx={50 + dx} cy="64" rx="14" ry="20" fill={`url(#${uid}-fruit)`} stroke={shade(a.fruit, -0.25)} strokeWidth="0.8" />)}</g>;
    return (
        <g>
            <path d="M14 86 Q30 70 40 76" stroke="#4f7a32" strokeWidth="1.5" fill="none" />
            <Leaf uid={uid} shape="heart" len={20} wid={14} x={18} y={80} rot={-70} color={a.leaf || '#4f8a3a'} serrated />
            {body}
            {shape === 'watermelon' && [-20, -10, 0, 10, 20].map((dx) => <path key={dx} d={`M${50 + dx} 40 Q${50 + dx * 1.4} 62 ${50 + dx} 84`} stroke="#1f4a2a" strokeWidth="3" fill="none" opacity="0.7" />)}
            {shape === 'melon' && Array.from({ length: 12 }, (_, i) => <path key={i} d={`M${30 + (i % 4) * 13} ${50 + Math.floor(i / 4) * 10} l6 3 l6 -3`} stroke={shade(a.fruit, 0.35)} strokeWidth="0.8" fill="none" />)}
            {shape === 'pumpkin' && <path d="M50 44 q-1 -8 4 -12" stroke="#5a6a3a" strokeWidth="3.5" fill="none" strokeLinecap="round" />}
            {shape === 'butternut' && <path d="M50 20 l0 -6" stroke="#6a7a3a" strokeWidth="3" />}
        </g>
    );
}

function Cob({ uid, a }) {
    return (
        <g>
            <path d="M50 90 L50 10" stroke="#6a8a3a" strokeWidth="3" />
            {[[-1, 70], [1, 56], [-1, 42], [1, 30]].map(([s, y], i) => <path key={i} d={`M50 ${y} Q${50 + s * 20} ${y - 14} ${50 + s * 34} ${y - 4}`} stroke="#5a8a3a" strokeWidth="3.5" fill="none" strokeLinecap="round" />)}
            <g transform="rotate(18 56 50)">
                <ellipse cx="60" cy="50" rx="7" ry="16" fill={`url(#${uid}-fruit)`} />
                {Array.from({ length: 24 }, (_, i) => <circle key={i} cx={56 + (i % 3) * 4} cy={38 + Math.floor(i / 3) * 3.2} r="1.5" fill={shade(a.fruit, 0.15)} stroke={shade(a.fruit, -0.25)} strokeWidth="0.3" />)}
                <path d="M53 58 Q56 70 60 70 Q64 70 67 58" fill="#9ac45a" />
                <path d="M60 34 q-3 -8 1 -12 M61 34 q3 -8 -1 -12" stroke="#c8903a" strokeWidth="0.8" fill="none" />
            </g>
        </g>
    );
}

function Spears({ uid, a }) {
    return (
        <g>
            <path d="M20 88 L80 88" stroke="#8a6a4a" strokeWidth="1.2" opacity="0.4" />
            {[-16, -6, 4, 14].map((dx, i) => (
                <g key={i}>
                    <path d={`M${50 + dx} 88 L${50 + dx + i - 1} ${26 + (i % 2) * 10}`} stroke={`url(#${uid}-leaf)`} strokeWidth="6" strokeLinecap="round" />
                    {[0, 1, 2].map((k) => <path key={k} d={`M${50 + dx - 3} ${40 + k * 12 + (i % 2) * 10} l3 -3 l3 3`} stroke={shade(a.leaf, -0.25)} strokeWidth="0.8" fill="none" />)}
                    {a.purple && <circle cx={50 + dx + i - 1} cy={26 + (i % 2) * 10} r="3" fill="#8a3a7a" />}
                </g>
            ))}
        </g>
    );
}

function Artichoke({ uid, a }) {
    return (
        <g>
            <path d="M50 90 L50 54" stroke="#6a8a6a" strokeWidth="3" />
            <Leaf uid={uid} shape="lance" len={26} wid={7} x={50} y={80} rot={200} color="#7a9a8a" serrated />
            <Leaf uid={uid} shape="lance" len={26} wid={7} x={50} y={74} rot={-20} color="#7a9a8a" serrated />
            {[[0, 50, 16], [0, 42, 13], [0, 34, 10], [0, 27, 6]].map(([, y, w], i) => (
                <g key={i}>{[-1, 0, 1].map((k) => <path key={k} d={`M${50 + k * w * 0.6 - w * 0.4} ${y} Q${50 + k * w * 0.6} ${y - 10} ${50 + k * w * 0.6 + w * 0.4} ${y}Z`} fill={`url(#${uid}-fruit)`} stroke={shade(a.fruit, -0.3)} strokeWidth="0.6" />)}</g>
            ))}
            {a.flower && <path d="M44 24 l2 -8 M48 23 l0 -9 M52 23 l0 -9 M56 24 l-2 -8" stroke={a.flower} strokeWidth="2" />}
        </g>
    );
}

function Bush({ uid, a, rnd }) {
    // coffee / tea / olive / goji style shrubs with small fruits or flowers
    return (
        <g>
            <path d="M50 90 L50 50 M50 64 L36 48 M50 58 L64 44" stroke="#6d5237" strokeWidth="2.5" strokeLinecap="round" />
            {Array.from({ length: 16 }, (_, i) => <Leaf key={i} uid={uid} shape={a.leafShape || 'lance'} len={a.leafLen || 12} wid={a.leafWid || 5} x={30 + rnd() * 40} y={26 + rnd() * 34} rot={rnd() * 360} color={a.leaf || '#3f7a3a'} />)}
            {a.fruit && Array.from({ length: a.n || 7 }, (_, i) => <circle key={i} cx={34 + rnd() * 32} cy={34 + rnd() * 26} r={a.size || 2.6} fill={`url(#${uid}-fruit)`} />)}
            {a.flower && Array.from({ length: 5 }, (_, i) => <circle key={`f${i}`} cx={34 + rnd() * 32} cy={28 + rnd() * 20} r="2.4" fill={a.flower} stroke="#f2d23a" strokeWidth="0.8" />)}
        </g>
    );
}

const PLANT_DRAWERS = {
    sprig: (p) => <Sprig {...p} />,
    feather: (p) => <Feather {...p} />,
    umbel: (p) => <UmbelPlant {...p} />,
    blades: (p) => <Blades {...p} />,
    rosette: (p) => <Rosette {...p} />,
    flower: (p) => <FlowerHead {...p} />,
    berries: (p) => <Berries {...p} />,
    nut: (p) => <Nut {...p} />,
    conifer: (p) => <Conifer {...p} />,
    root: (p) => <Root {...p} />,
    trefoil: (p) => <Trefoil {...p} />,
    cactus: (p) => <Cactus {...p} />,
    fruit: (p) => <Fruit {...p} />,
    stalks: (p) => <Stalks {...p} />,
    crop: (p) => <Crop {...p} />,
    tree: (p) => <FruitTree {...p} />,
    potted: (p) => <Potted {...p} />,
    bloom: (p) => <Bloom {...p} />,
    head: (p) => <Head {...p} />,
    bulb: (p) => <Bulb {...p} />,
    tuber: (p) => <Tuber {...p} />,
    gourd: (p) => <Gourd {...p} />,
    cob: (p) => <Cob {...p} />,
    spears: (p) => <Spears {...p} />,
    artichoke: (p) => <Artichoke {...p} />,
    bush: (p) => <Bush {...p} />,
};

/** Render a species illustration from its look spec. */
export default function SpeciesArt({ look, seed = '', className = '', title }) {
    const raw = useId();
    const uid = `sp${raw.replace(/[^a-zA-Z0-9]/g, '')}`;
    const rnd = useMemo(() => seeded(`${seed}-${look?.k}`), [seed, look?.k]);
    if (!look) return null;
    const a = {
        cap: '#a07a4f', under: undefined, stem: '#efe6d6', leaf: '#4f8a3a', fruit: '#7a2a3a', flower: undefined,
        ...look,
    };
    const drawer = MUSHROOM_DRAWERS[a.k] || PLANT_DRAWERS[a.k];
    if (!drawer) return null;
    const isMushroom = !!MUSHROOM_DRAWERS[a.k];
    return (
        <svg viewBox="0 0 100 100" className={className} role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true}>
            <defs>
                <Grad id={`${uid}-cap`} color={a.cap} />
                <LinGrad id={`${uid}-stem`} color={a.k === 'funnel' || a.k === 'trumpet' ? a.stem || a.cap : a.stem} />
                <Grad id={`${uid}-leaf`} color={a.leaf} cx="30%" cy="30%" />
                <Grad id={`${uid}-fruit`} color={a.fruit} />
                <Grad id={`${uid}-center`} color={a.k === 'bloom' && a.type === 'tulip' ? (a.flower || '#e0445a') : (a.center || '#e8b82a')} />
            </defs>
            <Shadow w={isMushroom ? 26 : 22} />
            {drawer({ uid, a, rnd })}
        </svg>
    );
}
